import UnexpectedSalonTypeError from "@errors/UnexpectedSalonTypeError"
import useSalonController from "@general/api/useSalonController"
import { route } from "@helpers"
import useAttribute from "@hooks/useAttribute"
import _ from "lodash"
import React from 'react'
import useApi from "./hooks/useApi"

export default function useSalon(slug: string): General.Models.Salon {
    const controller = useSalonController()
    const { data: state, setData: setState, fresh } = controller.show(slug).use(`general:salon:${slug}`)

    const api = useApi(slug, controller)
    const actions = useActionsAttribute(state)
    const maxScore = useMaxScoreAttribute(state)
    const organizations = useOrganizationsAttribute(state)

    return { 
        state, 
        setState, 
        methods: {
            fresh
        },
        api,
        attrs: {
            organizations,
            maxScore,
            actions
        }
    }
}

function useOrganizationsAttribute(salon: General.Resources.Salon | undefined): General.Models.Salon.Attrs.Organizations | null {
    return useAttribute(salon, salon => {
        const organizations = salon.patronages.map(patronage => patronage.organization)
        const array = _.uniqBy(organizations, value => value.id)
        const string = array.map(organization => organization.name).join(', ')
        const has = (name: string) => Boolean(organizations.find(organization => organization.name === name))
        return { array, string, has }
    })
}

function useMaxScoreAttribute(salon: General.Resources.Salon | undefined): General.Models.Salon.Attrs.MaxScore | null {
    return useAttribute(salon, salon => salon.juries.length * 10)
}

function useActionsAttribute(salon: General.Resources.Salon | undefined): General.Models.Salon.Attrs.Actions | null {
    return useAttribute(salon, salon => {
        switch (salon.type) {
            case 'voting':
                return {
                    primary: {
                        to: route("signin"),
                        label: "Sign in",
                        description: <>It's an ongoing salon. Sign in to see your entry or other salons.</>    
                    },
                    secondary: {
                        to: route("general:salon:conditions", salon.slug),
                        label: "Conditions"
                    }
                }
            case 'future':
            case 'current': 
                return {
                    primary: {
                        to: route("customer:salon", salon.id),
                        label: "Participate",
                        description: <>By participating, you acknowledge you've agreed to <a href={route("general:salon:conditions", salon.slug)} target="_blank">conditions</a>.</>    
                    },
                    secondary: {
                        to: route("general:salon:conditions", salon.slug),
                        label: "Conditions"
                    }
                }
            case 'finished':
                return {
                    primary: {
                        to: route("customer:salon", salon.id),
                        label: "My Results",
                        description: <>Log in to your account to check your awards & acceptances.</>    
                    },
                    secondary: {
                        to: route("general:salon:gallery", salon.slug),
                        label: "Gallery"
                    }
                }
            default:
                throw new UnexpectedSalonTypeError(salon.type)
        }
    })
}